import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Location } from "@angular/common";
import { CoreLogService } from "../../services/log.service";
import { Router } from "@angular/router";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { CoreLanguageService } from "../../services/language.service";
import { FormControl } from "@angular/forms";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { IOSHelperService } from "../../services/IOSHelper.service";
import Utils from "../../shared/utils";
import { Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { ErrorValidationComponent } from "../../directives/errorValidation/errorValidation.component";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { WarningdialogComponent } from "../../modalDialogs";
import { UserHelperService } from "../../services/userHelper.service";
import { UserProfileService } from "../userProfile/userprofile.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { RemoteOrderGroupDefineNameService } from "./remoteOrderGroupDefineName.service";
import { forbiddenNameValidator, ValidatorsConfig } from "../../shared/validator/validator.function";
import { WebShopService } from "../../services/webShop.service";
import { FieldName } from "../../shared/validator/validatorMessage.interface";
import { REMOTE_GROUPORDER_NAME_ERROR_MESSAGES } from "../../shared/validator/validatorErrorMessages";

@Component({
  selector: "remoteOrderGroupDefineName",
  templateUrl: "./remoteOrderGroupDefineName.component.html",
  styleUrls: ["./remoteOrderGroupDefineName.component.scss"],
  providers: [RemoteOrderGroupDefineNameService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class RemoteOrderGroupDefineName implements OnInit, OnDestroy {
  private _langSubscriber: Subscription;
  private _groupNameInputSubscriber: Subscription;
  private _groupNamevalidationSubscriber: Subscription;
  private _userNameInputSubscriber: Subscription;
  private _userNamevalidationSubscriber: Subscription;

  public canStartDefineGroup$: Observable<boolean>;
  public inputGroupName: string = "";
  public inputUserNickname: string = "";
  public isLogin: boolean = false;
  public isSmallView: boolean = false;
  public field = FieldName; // Expose FieldName enum to template

  @ViewChild("errorValidation") errorValidation: ErrorValidationComponent;
  /** Validate user input (user name)*/
  public userNameInputFormControl = new FormControl('', {
    validators: ValidatorsConfig.forGroupMemberName(),
    asyncValidators: forbiddenNameValidator(this._webShopService),
  });

  /** Validate user input (group name)*/
  public groupNameInputFormControl = new FormControl('', {
    validators: ValidatorsConfig.forRemoteOrderGroupName(),
    asyncValidators: forbiddenNameValidator(this._webShopService),
  });

  constructor(private _location: Location
    , private _router: Router
    , private _webShopService: WebShopService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _mainnavService: MainnavService
    , private _internationalisationService: InternationalisationService
    , private _remoteOrderGroupDefineNameService: RemoteOrderGroupDefineNameService
    , private _iosHelperService: IOSHelperService
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _userProfileService: UserProfileService
    , private _userHelpService: UserHelperService
    , private _overlaySpinnerService: OverlaySpinnerService) {
    const me = this;
    me.isSmallView = me._mainnavService.viewType === ViewType.Small;
    me.canStartDefineGroup$ = me._remoteOrderGroupDefineNameService.canStartDefineGroup$;
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._groupNameInputSubscriber) {
      me._groupNameInputSubscriber.unsubscribe()
    }
    if (me._userNameInputSubscriber) {
      me._userNameInputSubscriber.unsubscribe();
    }
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe()
    }
    if (me._groupNamevalidationSubscriber) {
      me._groupNamevalidationSubscriber.unsubscribe()
    }
    if (me._userNamevalidationSubscriber) {
      me._userNamevalidationSubscriber.unsubscribe()
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("RemoteOrderGrouDefineName.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;

    me._remoteOrderGroupDefineNameService.initial().then(() => {
      me.PushChanges();
    });

    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    //get latest validation status of that control whenever validation status is recalculated for that control.
    me._groupNamevalidationSubscriber = me.groupNameInputFormControl.statusChanges.subscribe(() => {
      me.PushChanges();
    });

    //get latest value of that control whenever there is any change. 
    me._groupNameInputSubscriber = me.groupNameInputFormControl.valueChanges.subscribe(() => {
      me.groupNameInputFormControl.markAsTouched();
      me.inputGroupName = me.groupNameInputFormControl.value;
      me.PushChanges();
    });

    //Subscribe to status changes in the user name input field.
    //When the status changes, trigger the PushChanges method 
    me._userNamevalidationSubscriber = me.userNameInputFormControl.statusChanges.subscribe(() => {
      me.updateGroupNameByUserName();
      me.PushChanges();
    });

    //get latest value of that control whenever there is any change. 
    me._userNameInputSubscriber = me.userNameInputFormControl.valueChanges.subscribe(() => {
      me.inputUserNickname = me.userNameInputFormControl.value;
      me.userNameInputFormControl.markAsTouched();
      me.PushChanges();
    })
    me.GetUserProfileName();
  }

  /** Get active error message for a form control based on field type */
  public activeError(control: FormControl, fieldName: FieldName): string | null {
    return ValidatorsConfig.getActiveErrorMessage(control, REMOTE_GROUPORDER_NAME_ERROR_MESSAGES, fieldName);
  }

  /** placeholder user name */
  public get userNameInputPlaceholder(): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_DefineUserName_Info_PlaceHolder")
  }

  /** placeholder group name */
  public get groupNameInputPlaceholder(): string {
    const me = this;
    return me._coreLanguageService.value("phRemoteOrder_DefineGroupName_Info_PlaceHolder");
  }

  /** Get user name and set*/
  public GetUserProfileName(): void {
    const me = this;
    me.isLogin = me._userHelpService.IsRegistered;
    me.inputUserNickname = me.userNameInputFormControl.value;
    if (me.isLogin) {
      me._userProfileService.GetUserInfoViewModel()
        .then(async (userViewModel) => {
          me.inputUserNickname = userViewModel.fname;
          me.userNameInputFormControl.setValue(me.inputUserNickname);
          me.PushChanges();
        })
        .catch(e => {
          me._coreLogService.error("RemoteOrderGrouDefineName.GetUserProfileName", "GetUserInfoViewModel", e);
        });
    }
  }

  public updateGroupNameByUserName() {
    const me = this;
    const userName = me.userNameInputFormControl.value ? me.userNameInputFormControl.value.trim() : "";
    const isValid = me.userNameInputFormControl.valid;
    if (!isValid || !userName) return;
    const autoGroupName = me._coreLanguageService
      .value("phRemoteOrder_GroupName_display_label")
      .replace("{username}", userName);

    // Only auto-fill if group name hasn't been changed manually
    if (!me.groupNameInputFormControl.dirty || !me.groupNameInputFormControl.value) {
      me.groupNameInputFormControl.setValue(autoGroupName);
      me.groupNameInputFormControl.markAsTouched();
      me.inputGroupName = autoGroupName;
      me.PushChanges();
    }
  }

  /**
   * Attempts to create a group and navigates to the success page.
   * Handles different outcomes: success, max group count reached, or failure.
   */
  public async CreateGroupAndNavigate(): Promise<void> {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    let canCreateGroup = false;
    me._remoteOrderGroupDefineNameService.canStartDefineGroup$.pipe(take(1)).subscribe(value => canCreateGroup = value).unsubscribe();
    if (canCreateGroup) {
      me._overlaySpinnerService.HideLayoutSpinner();
      await me._remoteOrderGroupDefineNameService.defineGroup(me.inputUserNickname, me.inputGroupName);
      me._router.navigate(["remoteOrderGroupDefineTime"]);
    } else {
      me.OpenWarningMaxGroupCountReached();
    }
  }

  /**
   * Checks if the button should be active
   * @returns true if both username and group name inputs are valid.
   */
  public setButtonActive(): boolean {
    const me = this;
    const validUserNameInput = !Utils.IsNullOrEmpty(me.userNameInputFormControl.value) && me.userNameInputFormControl.valid;
    const validGroupName = !Utils.IsNullOrEmpty(me.userNameInputFormControl.value) && me.groupNameInputFormControl.valid;
    return validUserNameInput && validGroupName;
  }

  /**
 * Opens a warning dialog when the maximum number of group orders has been reached.
 * After the dialog is dismissed, the user is redirected to the home page.
 */
  private OpenWarningMaxGroupCountReached() {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phRemoteOrder_Dialog_GroupMaxCountReached_Title"
        , content: "hRemoteOrder_Dialog_GroupMaxCountReached_Info"
        , btnLabel: "phGlobal_Continue"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
    warningButtomSheet.afterDismissed().subscribe((result) => {
      if (result) {
        me._router.navigate(["/"]);
      }
    });
  }

  public setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  /** navigate back on swipe from left to right */
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

  /** navigate back */
  public navigateBack(): void {
    const me = this;
    me._location.back();
  }
}